import React, { Component, ReactNode } from 'react'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

interface ErrorMessageProps {
  message: string
  className?: string
}

const ErrorMessage = ({ message, className }: ErrorMessageProps) => {
  return (
    <div
      className={cn(
        'flex items-center gap-2 p-4 bg-destructive/10 border border-destructive/20 rounded-md',
        className
      )}
    >
      <AlertCircle className="w-5 h-5 text-destructive" />
      <p className="text-sm text-destructive">{message}</p>
    </div>
  )
}

// Enhanced Error Boundary for Capacity Operations
interface CapacityErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
}

interface CapacityErrorBoundaryProps {
  children: ReactNode
  context?: string
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
  // Note: fallback prop removed - fallback mechanisms are prohibited per CLAUDE.md
  // All errors must be transparent and actionable, not hidden behind fallbacks
}

class CapacityErrorBoundary extends Component<
  CapacityErrorBoundaryProps,
  CapacityErrorBoundaryState
> {
  constructor(props: CapacityErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(
    error: Error
  ): Partial<CapacityErrorBoundaryState> {
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('CapacityErrorBoundary caught an error:', error, errorInfo)

    this.setState({
      error,
      errorInfo,
    })

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo)
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  render() {
    if (this.state.hasError) {
      // CRITICAL: No fallback mechanisms - show transparent error immediately
      // This ensures users see the actual problem instead of masked fallback UI

      const errorMessage = this.state.error?.message || 'Unknown error occurred'
      const contextMessage = this.props.context
        ? `${this.props.context} failed: ${errorMessage}`
        : `Capacity operation failed: ${errorMessage}`

      // Transparent error display - no fallback UI, no masking
      return (
        <div className="flex flex-col items-start gap-4 p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-destructive mt-0.5 shrink-0" />
            <div className="space-y-2">
              <h4 className="font-semibold text-destructive">
                Error in {this.props.context || 'Capacity Operation'}
              </h4>
              <p className="text-sm text-destructive font-semibold">
                {errorMessage}
              </p>
              {/* Always show technical details for transparent error handling */}
              <details className="text-sm text-muted-foreground">
                <summary className="cursor-pointer font-semibold text-destructive/80">
                  Technical Details (Click to expand)
                </summary>
                <pre className="mt-2 p-2 bg-destructive/5 border border-destructive/20 rounded text-left overflow-auto whitespace-pre-wrap">
                  Context: {this.props.context || 'Capacity Operation'}
                  Error: {this.state.error?.message || 'Unknown error'}
                  Stack: {this.state.error?.stack || 'No stack trace available'}
                  Component Stack:{' '}
                  {this.state.errorInfo?.componentStack ||
                    'No component stack available'}
                </pre>
              </details>
              <div className="pt-2">
                <p className="text-sm text-muted-foreground">
                  <strong>Resolution:</strong> Check the compartment
                  configuration above. Ensure all required fields are filled
                  correctly.
                </p>
              </div>
            </div>
          </div>
          <Button
            onClick={this.handleRetry}
            variant="destructive"
            size="sm"
            className="flex items-center gap-2 self-start"
          >
            <RefreshCw className="w-4 h-4" />
            Retry Operation
          </Button>
        </div>
      )
    }

    return this.props.children
  }
}

// Hook-based Error Boundary for functional components
interface UseErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export const useErrorBoundary = () => {
  const [state, setState] = React.useState<UseErrorBoundaryState>({
    hasError: false,
    error: null,
  })

  const resetError = React.useCallback(() => {
    setState({ hasError: false, error: null })
  }, [])

  const catchError = React.useCallback((error: Error, context?: string) => {
    // Enhanced error logging with context for transparent debugging
    console.error(
      `useErrorBoundary caught error${context ? ` in ${context}` : ''}:`,
      {
        message: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
        context: context || 'Unknown context',
      }
    )
    setState({ hasError: true, error })
  }, [])

  // Error boundary for async operations with transparent error propagation
  const withErrorBoundary = React.useCallback(
    <T extends any[], R>(fn: (...args: T) => Promise<R>, context?: string) => {
      return async (...args: T): Promise<R | null> => {
        try {
          resetError()
          return await fn(...args)
        } catch (error) {
          // CRITICAL: No error masking - always propagate the real error
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error occurred'

          const contextualError = new Error(
            context ? `${context}: ${errorMessage}` : errorMessage
          )

          // Preserve original stack trace for debugging
          if (error instanceof Error) {
            contextualError.stack = error.stack
          }

          catchError(contextualError, context)

          // IMPORTANT: Return null but ensure error is visible to user
          // This follows transparent error principle - no silent failures
          return null
        }
      }
    },
    [catchError, resetError]
  )

  // Enhanced error display function for UI components
  const renderErrorMessage = React.useCallback(
    (context?: string) => {
      if (!state.hasError || !state.error) return null

      return (
        <div className="flex flex-col gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-destructive">
                {context && `${context}: `}
                {state.error.message}
              </p>
              <details className="text-sm text-muted-foreground mt-2">
                <summary className="cursor-pointer">Technical Details</summary>
                <pre className="mt-2 p-2 bg-destructive/5 rounded text-left overflow-auto text-sm">
                  {state.error.stack || 'No stack trace available'}
                </pre>
              </details>
            </div>
          </div>
          <button
            onClick={resetError}
            className="text-sm text-destructive hover:text-destructive/80 self-start underline"
          >
            Dismiss Error
          </button>
        </div>
      )
    },
    [state.hasError, state.error, resetError]
  )

  return {
    ...state,
    resetError,
    catchError,
    withErrorBoundary,
    renderErrorMessage,
  }
}

export { ErrorMessage, CapacityErrorBoundary }
